<template>
  <BaseModal
    useForm
    :heading="$t('global.payments.gifty')"
    small
    @submit="handleSubmit"
    @close="emit('close')"
  >
    <div :class="$style.base">
      <BaseInput
        v-model="code"
        :label="$t('global.code')"
        required
        focus
        v-test="'register-gifty-code'"
      />
      <BaseInput
        v-model="amount"
        type="currency"
        :label="$t('global.amount_currency')"
        required
        :minValue="1"
        :maxValue="outstandingAmount"
        v-test="'register-gifty-amount'"
      />
      <BaseText
        v-if="isAlreadyUsed"
        color="error"
        v-test="'register-gifty-already-used'"
      >
        {{ $t('giftcard.already_used') }}
      </BaseText>
    </div>

    <template #footer>
      <BaseButton
        color="inverted"
        @click="emit('close')"
        v-test="'register-gifty-close'"
      >
        {{ $t('global.actions.cancel') }}
      </BaseButton>
      <BaseButton
        submitForm
        :disabled="!normalizedCode || isAlreadyUsed"
        v-test="'register-gifty-save'"
      >
        {{ $t('global.actions.submit') }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script
  setup
  lang="ts"
>
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useRegisterOrderStore } from '@/modules/register/stores/order';

const props = defineProps<{
  paymentAmount: number;
}>();

const emit = defineEmits<{
  (
    e: 'submit',
    payment: { type: 'gifty'; amount: number; giftyCode: string; name: string },
  ): void;
  (e: 'close'): void;
}>();

const { order } = useRegisterOrderStore();
const { outstandingAmount } = storeToRefs(useRegisterOrderStore());

const code = ref('');
const amount = ref(Math.min(props.paymentAmount, outstandingAmount.value));

const normalizedCode = computed(() =>
  code.value.replace(/[^a-z0-9]/gi, '').toUpperCase(),
);

const isAlreadyUsed = computed(() =>
  order.transactions.some(
    (transaction) =>
      !transaction.id &&
      transaction.type === 'GIFTY' &&
      transaction.giftyCode === normalizedCode.value,
  ),
);

const handleSubmit = () => {
  emit('submit', {
    type: 'gifty',
    amount: amount.value,
    giftyCode: normalizedCode.value,
    name: normalizedCode.value,
  });
};
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;
  flex-direction: column;
  gap: $spacing;
}
</style>
